/**
 * arc.hive drop landing page — countdown timer, timezone-aware date/time,
 * and State 1 / State 2 toggle for subscribe vs. password-access views.
 *
 * Reads ISO datetime from [data-drop-datetime][data-iso] and [data-countdown][data-target].
 * Both use the browser's local timezone for display; countdown math compares
 * absolute timestamps so it's timezone-correct regardless of viewer location.
 */
(() => {
  const root = document.querySelector('.drop-page');
  if (!root) return;

  const SUBSCRIBE = 1;
  const ACCESS = 2;

  const state1 = root.querySelector('[data-state="1"]');
  const state2 = root.querySelector('[data-state="2"]');
  const datetimeContainer = root.querySelector('[data-drop-datetime]');
  const dateEl = root.querySelector('[data-drop-date]');
  const timeEl = root.querySelector('[data-drop-time]');
  const countdownContainer = root.querySelector('[data-countdown]');
  const daysEl = root.querySelector('[data-countdown-days]');
  const hoursEl = root.querySelector('[data-countdown-hours]');
  const minsEl = root.querySelector('[data-countdown-mins]');

  // ---- State toggle ----
  const showState = (state) => {
    if (!state1 || !state2) return;
    state1.hidden = state === ACCESS;
    state2.hidden = state !== ACCESS;
  };

  // Initial state: ACCESS if URL hash matches OR if server rendered a password
  // error (Shopify re-renders the page with form.errors when password is wrong).
  // Error detection avoids a longer State 1 flash before the user sees their error.
  const hasPasswordError = !!root.querySelector('.drop-state-2__error');
  const initialState = (window.location.hash === '#access' || hasPasswordError) ? ACCESS : SUBSCRIBE;
  showState(initialState);

  root.querySelectorAll('[data-toggle-access]').forEach((el) => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      window.history.replaceState(null, '', '#access');
      showState(ACCESS);
    });
  });

  root.querySelectorAll('[data-toggle-subscribe]').forEach((el) => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      showState(SUBSCRIBE);
    });
  });

  // ---- Date / time display (user's local timezone) ----
  const isoString = datetimeContainer?.dataset.iso;
  if (isoString && dateEl && timeEl) {
    const dropDate = new Date(isoString);
    if (!isNaN(dropDate.getTime())) {
      try {
        dateEl.textContent = new Intl.DateTimeFormat(undefined, {
          year: 'numeric', month: 'long', day: 'numeric',
        }).format(dropDate);
        timeEl.textContent = new Intl.DateTimeFormat(undefined, {
          hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
        }).format(dropDate);
      } catch (_) {
        // Intl unavailable or formatting failed — leave em-dash placeholders.
      }
    }
    // Invalid ISO → placeholders unchanged. Silent, no crash.
  }

  // ---- Countdown ----
  const targetString = countdownContainer?.dataset.target;
  if (!targetString || !daysEl || !hoursEl || !minsEl) return;
  const target = new Date(targetString);
  if (isNaN(target.getTime())) return; // invalid ISO → leave "--", don't tick

  const pad = (n) => String(n).padStart(2, '0');
  let timer = null;
  const tick = () => {
    const diff = target - new Date();
    if (diff <= 0) {
      countdownContainer.textContent = 'the drop is live';
      if (timer) clearInterval(timer);
      return;
    }
    const totalMins = Math.floor(diff / 60000);
    const days = Math.floor(totalMins / (60 * 24));
    const hours = Math.floor((totalMins / 60) % 24);
    const mins = totalMins % 60;
    daysEl.textContent = pad(days);
    hoursEl.textContent = pad(hours);
    minsEl.textContent = pad(mins);
  };
  tick();
  timer = setInterval(tick, 1000);
})();

/**
 * Email validation + Klaviyo subscribe for the State 1 form.
 *
 * Native HTML5 validity is suppressed by `novalidate` on the form so we
 * control the messaging style. On valid submission, POSTs to Klaviyo's
 * /client/subscriptions/ endpoint (public, CORS-enabled, designed for
 * browser use — company_id S7gBVB is safe to expose). Tags the profile
 * with the current drop label so the next drop's subscribers can be
 * segmented separately while existing tags are preserved.
 */
(() => {
  const KLAVIYO_COMPANY_ID = 'S7gBVB';
  const KLAVIYO_LIST_ID = 'YASB4C';
  const KLAVIYO_REVISION = '2024-10-15';
  const KLAVIYO_ENDPOINT = `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`;
  const KLAVIYO_EVENT_ENDPOINT = `https://a.klaviyo.com/client/events/?company_id=${KLAVIYO_COMPANY_ID}`;

  const form = document.querySelector('.drop-state-1__form');
  if (!form) return;
  const input = form.querySelector('input[type="email"]');
  const error = form.querySelector('.drop-state-1__error');
  if (!input || !error) return;

  const showError = (msg) => {
    error.textContent = msg;
    error.hidden = false;
  };
  const clearError = () => {
    if (!error.hidden) error.hidden = true;
  };

  // Drop label source = the rendered .drop-tagline element ("HIVE 001"),
  // which is liquid-driven by settings.drop_number. When the merchant
  // bumps drop_number to "002", new subscribers auto-tag with "HIVE 002";
  // existing subscribers keep their original tag.
  const dropTagline = (document.querySelector('.drop-tagline')?.textContent || '').trim() || 'HIVE unknown';

  // Minimal HTML escape for the dropTagline interpolation in success state.
  // Value is from theme settings (trusted) but defensive habit — keeps the
  // outerHTML rewrite safe against future setting values containing markup.
  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));

  const showSuccessState = () => {
    const successHTML = `
      <div class="drop-state-1__success" role="status">
        <p class="drop-state-1__success-headline">You're in.</p>
        <p class="drop-state-1__success-body">We'll send you the password when ${escapeHtml(dropTagline)} opens.</p>
      </div>
    `;
    // outerHTML detaches the form (and its listeners). We've already done
    // the network round-trip and are committed to the success view.
    form.outerHTML = successHTML;
  };

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const val = input.value.trim();
    if (!val) {
      showError('This field is required');
      input.focus();
      return;
    }
    const looksValid = !input.validity.typeMismatch &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
    if (!looksValid) {
      showError('Please enter a valid email address');
      input.focus();
      return;
    }
    clearError();

    // Loading state. .button-text is display:none on mobile, so the label
    // change is desktop-visible; disabled + cursor still convey loading on mobile.
    const button = form.querySelector('button');
    const buttonTextEl = button?.querySelector('.button-text');
    const originalButtonText = buttonTextEl?.textContent;
    if (button) button.disabled = true;
    if (buttonTextEl) buttonTextEl.textContent = 'JOINING…';

    try {
      const response = await fetch(KLAVIYO_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': KLAVIYO_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: val,
                    properties: {
                      latest_drop_subscribed: dropTagline,
                      source: 'drop_landing_page',
                    },
                  },
                },
              },
            },
            relationships: {
              list: {
                data: { type: 'list', id: KLAVIYO_LIST_ID },
              },
            },
          },
        }),
      });

      // Klaviyo returns 202 Accepted for both new subscriptions AND
      // already-subscribed emails — treat both as success. No need to
      // surface "already subscribed" as a distinct state.
      if (response.ok || response.status === 202) {
        // Fire a "Subscribed to Drop" event so the profile's timeline
        // preserves per-drop history (subscribe call only stores the
        // LATEST drop on the profile; events accumulate forever).
        // Wrapped in its own try/catch — event-tracking failure is
        // non-critical, the subscription itself already succeeded.
        try {
          await fetch(KLAVIYO_EVENT_ENDPOINT, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'revision': KLAVIYO_REVISION,
            },
            body: JSON.stringify({
              data: {
                type: 'event',
                attributes: {
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: { email: val },
                    },
                  },
                  metric: {
                    data: {
                      type: 'metric',
                      attributes: { name: 'Subscribed to Drop' },
                    },
                  },
                  properties: {
                    drop_number: dropTagline,
                    source: 'drop_landing_page',
                  },
                },
              },
            }),
          });
        } catch (eventErr) {
          console.error('[drop-page] Event tracking error (non-critical):', eventErr);
        }

        showSuccessState();
        return;
      }
      throw new Error(`Klaviyo subscribe failed: ${response.status}`);
    } catch (err) {
      console.error('[drop-page] Subscribe error:', err);
      showError('Something went wrong. Please try again.');
      if (button) button.disabled = false;
      if (buttonTextEl && originalButtonText) buttonTextEl.textContent = originalButtonText;
    }
  });

  input.addEventListener('input', clearError);
})();
